import React from "react";
import { Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { FaEnvelope, FaRegCopy } from "react-icons/fa";
import TextInput from "shared/components/input/TextInput";
import CustomButton from "shared/components/button/CustomButton";
import ButtonConfirmarConfiguracao from "./ButtonConfirmarConfiguracao";

import { ConfigurarEmailUnicoForm } from "../ConfigurarEmailUnico"; 

interface FormularioEmailUnicoProps {
  /** Consulta de status em andamento. */
  isVerificando: boolean;
  /** Configuração sendo salva. */
  isSalvando: boolean;
  groupPayEmail: string;
  control: Control<ConfigurarEmailUnicoForm>;
  register: UseFormRegister<ConfigurarEmailUnicoForm>;
  setValue: UseFormSetValue<ConfigurarEmailUnicoForm>;
  onSigaOsPassosClick: () => void;
  onCopyToClipboard: () => void;
  onSubmit: () => void;
  onVerificarAgora: () => void;
  classes: Record<string, string>;
  statusAtual: string | null;
  dataHoraTeste: string | null;
  onConfirmacaoIniciada?: () => void;
}

const formatarHorario = (dataHora: string | null): string | null => {
  if (!dataHora) {
    return null;
  }

  const data = new Date(dataHora);
  if (isNaN(data.getTime())) {
    return null;
  }

  return data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const FormularioEmailUnico: React.FC<FormularioEmailUnicoProps> = ({
  isVerificando,
  isSalvando,
  groupPayEmail,
  control,
  register,
  setValue,
  onSigaOsPassosClick,
  onCopyToClipboard,
  onSubmit,
  onVerificarAgora,
  classes,
  statusAtual,
  dataHoraTeste,
  onConfirmacaoIniciada,
}) => {
  const horarioEnvio = formatarHorario(dataHoraTeste);

  return (
    <>
      <p className={classes.radioDescription}>
        Para usar o e-mail único, é necessário criar uma{" "}
        <strong>regra de redirecionamento do e-mail da administradora</strong> para o e-mail criado pelo Group Pay. Para
        isso, siga os passos abaixo.
      </p>

      <div className={classes.configSection}>
        <div className={classes.step}>
          <p className={classes.stepTitle}>1. Informe aqui o e-mail da administradora</p>
          <p className={classes.stepSubtitle}>O e-mail que sempre recebe as notas fiscais dos condomínios</p>
          <div className={classes.inputGroup}>
            <TextInput
              id="email"
              name="email"
              label="E-mail da administradora"
              placeholder="email@administradora.com.br"
              usarValidacaoParaFormulario
              control={control}
              register={register}
              setValue={setValue}
            />
          </div>
        </div>

        <div className={classes.step}>
          <p className={classes.stepTitle}>
            2. Copie o e-mail abaixo e crie uma regra de redirecionamento de mensagens
          </p>
          <p className={classes.stepSubtitle}>
            Acesse sua caixa de entrada e{" "}
            <span className={classes.linkAction} onClick={onSigaOsPassosClick}>
              siga os passos aqui
            </span>
            .
          </p>
          <div className={classes.copySection}>
            <div className={classes.emailDisplay}>
              <div className={classes.iconContainer}>
                <FaEnvelope size={16} />
              </div>
              <div className={classes.emailText}>
                <label>E-mail para regra</label>
                <span className={classes.emailContainer}>
                  {groupPayEmail}
                  <button
                    type="button"
                    onClick={onCopyToClipboard}
                    className={classes.copyButton}
                    title="Copiar e-mail"
                  >
                    <FaRegCopy size={18} />
                  </button>
                </span>
              </div>
            </div>
            {statusAtual === "PENDENTE_DE_VALIDACAO" ? (
              <ButtonConfirmarConfiguracao
                groupPayEmail={groupPayEmail}
                onConfirmacaoIniciada={onConfirmacaoIniciada}
                isTesting={isVerificando}
              />
            ) : statusAtual === "PENDENTE" ? (
              // Enquanto a validação não conclui, a tela consulta sozinha a cada minuto —
              // mas o usuário não fica refém desse ciclo: pode forçar a consulta ou refazer
              // a configuração a qualquer momento.
              <div className={classes.pendingBlock}>
                <div className={classes.pendingActions}>
                  <CustomButton
                    id="verificar-redirecionamento-btn"
                    label={isVerificando ? "Verificando..." : "Verificar agora"}
                    variant="primary"
                    disabled={isVerificando}
                    onClick={onVerificarAgora}
                    type="button"
                  />
                  <CustomButton
                    id="refazer-redirecionamento-btn"
                    label="Refazer configuração"
                    variant="outline-primary"
                    disabled={isVerificando || isSalvando}
                    onClick={onSubmit}
                    type="button"
                  />
                </div>
                {horarioEnvio && (
                  <span className={classes.pendingHint}>
                    Validação iniciada às {horarioEnvio}. A confirmação pode levar alguns minutos.
                  </span>
                )}
              </div>
            ) : (
              <CustomButton
                id="testar-regra-btn"
                label="Realizar redirecionamento"
                variant="primary"
                disabled={isVerificando || isSalvando}
                onClick={onSubmit}
                type="button"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FormularioEmailUnico;
